import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";

/**
 * Server-Sent Events listener voor `storefront.mv.refreshed` op channel `STOREFRONT`.
 *
 * Bij ontvangst: invalideert React Query keys voor product-listing zodat de grid
 * binnen ~12s actuele data toont na een PIM-edit (ADR-011 v2 §3 SSE-publish).
 *
 * In mock-mode wordt de EventSource niet opgezet — de mock-data is sowieso statisch.
 *
 * Endpoint: backend gebruikt `/api/events/stream?channels=*` (events.py). Onbekende
 * channel-namen worden door de backend gefilterd; `*` ontvangt alle kanalen incl.
 * de storefront-publish (`eventbus:storefront`).
 *
 * Fallback: bij meer dan 3 mislukte verbindingen sluiten we de SSE permanent
 * om eindeloze retry-loops bij anonieme bezoekers te voorkomen (anders blijft de
 * browser automatisch herverbinden).
 */
export function useStorefrontRefreshSse(): void {
  const { isAuthed } = useAuth();
  const queryClient = useQueryClient();
  const failureCountRef = useRef(0);

  useEffect(() => {
    if (import.meta.env.VITE_MOCK_API === "true") return;
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    const url = `${base}/api/events/stream?channels=*`;

    let sse: EventSource | null = null;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      try {
        sse = new EventSource(url, { withCredentials: false });
      } catch (err) {
        if (import.meta.env.DEV) {
          console.debug("[SSE] kon niet opzetten:", err);
        }
        return;
      }

      const onRefresh = () => {
        failureCountRef.current = 0;
        queryClient.invalidateQueries({ queryKey: ["storefront", "products"] });
      };
      sse.addEventListener("storefront.mv.refreshed", onRefresh);

      sse.onopen = () => {
        failureCountRef.current = 0;
      };

      sse.onerror = () => {
        failureCountRef.current += 1;
        if (import.meta.env.DEV) {
          console.debug(
            `[SSE] verbinding verbroken (poging ${failureCountRef.current})`,
          );
        }
        // Bij >3 mislukte verbindingen: sluit permanent (endpoint mist/ongeauth.).
        if (failureCountRef.current > 3 && sse) {
          sse.close();
          sse = null;
          if (import.meta.env.DEV) {
            console.debug(
              "[SSE] te veel fouten, herverbinding uitgeschakeld voor deze sessie",
            );
          }
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      if (sse) sse.close();
    };
  }, [isAuthed, queryClient]);
}
